import React, {useState} from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../../components/content-box/ContentBox";
import PageActions from "../../../../../base/PageActions";
import Breadcrumbs from "../../../../../../components/breadcrumbs/Breadcrumbs";
import Modal from "../../../../../../components/modal/Modal";
import _image_service from "../../../../../../services/ImageService";
import '../../../../../styles/Common.scss';
import '../../WorldPages.scss';
import '../../WorldDynamicContent.scss';

interface CreationHistoryPageProps {
}

const CreationHistoryPage = (props: CreationHistoryPageProps) => {

    const _map_src = `chase/world/geography/casarok_pre_cataclysm.png`;

    const page: PageActions = new PageActions({
        title: `Casarok History: Creation Age - Chase the Fox`,
        description: "The creation age of Casarok covers the time before intelligent mortal life populated the world!",
        keywords: ["casarok", "casarok history", "creation age", "fantasy timeline", "fantasy world history", "fantasy creation myth", "fantasy creation story"],
        imgSrc: _map_src,
        url: `world/history/creation-age`
    });

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

    return page.generateMetaContent(
        <>
            <Modal isOpen={modalIsOpen}
                   onClose={() => setModalIsOpen(false)}>
                <div className="geography-modal-view">
                    <p className="center">
                        <a href={_image_service.getCdnImageSrc(_map_src, 'full')}
                           target="_blank"
                           rel="noopener noreferrer">
                            Click here to view the full size image.
                        </a>
                    </p>
                    <img src={_image_service.getCdnImageSrc(_map_src, 'desktop')}
                         title='Casarok Pre-Cataclysm Map'
                         alt="A map of Casarok before the Great Cataclysm"/>
                </div>
            </Modal>
            <ContentBox header="The Creation Era of Casarok">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <a id="visible-element"
                       className="switch-visible geography-modal-link"
                       onClick={() => setModalIsOpen(true)}>
                        <img className="block width-50-percent"
                             src={_image_service.getCdnImageSrc(_map_src)}
                             title='Casarok Pre-Cataclysm Map'
                             alt="A map of Casarok before the Great Cataclysm"/>
                    </a>
                </div>
                <div className="world-page-static">
                    <p className="center">
                        <em>This age covers the time before 10,000 BCE.</em>
                    </p>
                    <p>
                        The Creation Era of Casarok covers the expansive time before intelligent life in the world. This
                        timespan includes the events of
                        the <NavLink className="world-link" to="/world/religion/pantheon">pantheon</NavLink> of
                        Casarok, the creation of the world, and the evolution of various creatures that dwell within
                        it. There was obviously never any recorded history of this era. Only those who could commune
                        with the gods and goddesses in the coming ages would have any knowledge of these events. Other
                        than that direct communication, it would be up to archaeologists and historians to find and
                        catalog them.
                    </p>
                    <p>
                        Any creatures originating in this era are designated as primordial. Primordial beings mostly
                        come from other worlds. All creatures came from creations of the gods and goddesses themselves,
                        but not all of them were created on Casarok itself. Many modern day organisms on Casarok evolved
                        from these primordial creatures. In fact, the evolution of the
                        modern <NavLink className="world-link" to="/world/bestiary/draconic">Draconic</NavLink> race
                        marked the end of this era in the Casarok history books.
                    </p>
                    <p>
                        <em>How does Casarok's Creation Era differ from prehistory on Earth?</em><br/>
                        The gods and goddesses of Casarok directly influenced the creation of the world, imbuing it with
                        magic and populating it with various creatures. Rather than having a world of dinosaurs or
                        giant arthropods, for example, all creatures lived in various places in the world. Some would
                        die off and become extinct. Others would thrive and evolve.
                    </p>
                    <h3 className="headline">Notable Events (in order of their occurrence)</h3>
                    <ul className="spaced">
                        <li>
                            The universe was created. The gods and goddesses, beings of pure mana and immense power,
                            were created by their progenitors to populate the empty void.
                        </li>
                        <li>
                            The gods and goddesses forged the various components of the universe from the dust and
                            debris of its creation. Stars, planets, and other forces visible or unseen populated the
                            empty void.
                        </li>
                        <li>
                            Some of the planets were populated with life, but none of the early ones survived. They were
                            either forgotten or destroyed either by disputes or universal forces.
                        </li>
                        <li>
                            The pantheon fought amongst themselves for power. A pact was formed by the four strongest
                            that gave each god or goddess their own domain of influence. Overreaching into another's
                            domain would be seen as a crime and tried by the leader and arbiter, Tianlong.
                        </li>
                        <li>
                            Many more worlds are created. One is a world of dragons crafted in the fashion of the
                            Dragon Deities. Another is a world of diverse animals.
                        </li>
                        <li>
                            With the entire pantheon unable to come to a consensus on a favorite world,
                            the <NavLink className="world-link" to="/world/religion/mythology/myth-of-the-six-sisters">Six
                            Sisters</NavLink> are tasked with creating some of their own.
                        </li>
                        <li>
                            The Six Sisters create Casarok and are punished for fighting over it. Still, the gods and
                            goddesses all covet this new world and move many of their own created lifeforms here to
                            populate it.
                        </li>
                        <li>
                            Many organisms thrive here, and the world becomes vibrant and teeming with life.
                        </li>
                        <li>
                            Concerned for the plant life on Casarok, two divine lovers come together to hyper-evolve
                            them. Tempatrius, the god of time, and Domathair, the goddess of nature, accidentally create
                            the <NavLink className="world-link" to="/world/bestiary/floradon">the floradons</NavLink> among
                            the new plant life.
                        </li>
                        <li>
                            The <NavLink className="world-link" to="/world/geography">Eldwood</NavLink> expands far
                            beyond its modern or even pre-Cataclysmic boundaries.
                        </li>
                        <li>
                            Some species of dragons begin to evolve, becoming more intelligent and standing on two legs.
                            They are the ancestors of the Draconic race.
                        </li>
                        <li>
                            The Draconic race is first seen at about the 10,000 BCE mark, though the exact date is
                            disputed by several thousand years among the scholars of Casarok.
                        </li>
                    </ul>
                </div>
            </ContentBox>
        </>
    );
}

export default CreationHistoryPage;